import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { GridItem, Text, Flex, Box, Container } from '@chakra-ui/react';

import ProductCard from '@repo/ui-components/tailwind/ProductCard';
import MediaBackground from '@repo/ui-components/dept/molecules/MediaBackground';
import { generateSectionId } from '@repo/ui-components/dept/utils/generateSectionId';
import CTAButton from '@repo/ui-components/tailwind/CTAButton';
import { pushToDataLayer } from '@repo/utils';
import useOnFirstView from '@repo/ui-components/hooks/useOnFirstView';

function MarketingMoment({ block }) {
  const {
    title,
    description,
    background,
    cta,
    inverted,
    product,
    promotionId,
  } = block;

  const ref = useRef();
  const hasProduct = product?.productReference;

  const handleInverted = (defaultVal, invertedVal) => ({
    base: defaultVal,
    md: inverted ? invertedVal : defaultVal,
  });

  const handleDataLayer = event => {
    pushToDataLayer(event, {
      ecommerce: {
        currency: 'USD',
        value: product?.productReference?.shopifyProduct?.variants[0].price,
        promotion_id: promotionId,
      },
      items: [
        {
          variant: product?.productReference?.shopifyProduct?.variants[0],
          product: product?.productReference?.shopifyProduct,
        },
      ],
    });
  };

  useOnFirstView({
    callback: () => handleDataLayer('view_promotion'),
    observerProps: { threshold: 0.5 },
    ready: !!block,
    ref,
  });

  return (
    <Container
      {...generateSectionId(block)}
      py={{ base: 14, md: 40, lg: 20 }}
      ref={ref}
    >
      <Box
        pos="relative"
        w="100%"
        zIndex={2}
        display={['flex', null, 'block']}
        flexDirection="column-reverse"
      >
        {hasProduct && (
          <Box
            pos={['relative', null, 'absolute']}
            w={['auto', null, 300]}
            shadow={{ base: 'none', lg: 'lg' }}
            zIndex={3}
            top="50%"
            transform={['none', null, 'translateY(-50%)']}
            left={handleInverted(0, 'auto')}
            right={handleInverted('auto', 0)}
            borderRadius="lg"
            border={['1px solid', null, '0px']}
            borderColor="stroke.light"
          >
            <ProductCard
              onClick={() => handleDataLayer('select_promotion')}
              product={product.productReference}
            />
          </Box>
        )}
        <MediaBackground
          inverted={hasProduct ? inverted : false}
          as={Flex}
          background={background}
          align="center"
          flexDirection={{
            base: 'column-reverse',
            lg: inverted ? 'row-reverse' : 'row',
          }}
          mb={[6, null, 0]}
          ml={[0, null, hasProduct ? handleInverted(150, 0) : 0]}
          mr={[0, null, hasProduct ? handleInverted(0, 150) : 0]}
          w="auto"
          p={[6, null, 16]}
          pt={[6, null, 'auto']}
          h={{ base: '28rem', md: '44rem', lg: '38rem' }}
        >
          <GridItem
            as={Flex}
            rowSpan={1}
            gridColumnStart={{
              base: 1,
              md: handleInverted(3, 1),
              lg: handleInverted(6, 1),
            }}
            gridColumnEnd={{
              base: 12,
              md: handleInverted('none', 5),
              lg: handleInverted('none', 9),
            }}
            alignItems={[
              'flex-start',
              null,
              handleInverted('flex-end', 'flex-start'),
            ]}
            textAlign={['left', null, handleInverted('right', 'left')]}
            h="full"
            direction="column"
            justify="end"
            zIndex={1}
          >
            {title && (
              <Text
                as="h2"
                textStyle="h1"
                fontWeight="extrabold"
                color="typography.lighter"
                mb={4}
              >
                {title}
              </Text>
            )}

            {description && (
              <Text
                textStyle="bodyLarge"
                color="typography.lighter"
                fontWeight="bold"
                mb={8}
              >
                {description}
              </Text>
            )}

            {cta?.text && (
              <CTAButton
                cta={cta}
                onClick={() => handleDataLayer('select_promotion')}
              >
                {cta.text}
              </CTAButton>
            )}
          </GridItem>
        </MediaBackground>
      </Box>
    </Container>
  );
}

MarketingMoment.propTypes = {
  block: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    alt: PropTypes.string,
    image: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
}.isRequired;

export default MarketingMoment;
